import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Theme } from '@mui/material/styles';
import { getThemePrimaryColor, updateThemePrimaryColor } from '@modules/enterprise';
import {
  buildTheme,
  DEFAULT_PRIMARY,
  getInitialMode,
  getSystemMode,
  type ThemeMode,
} from './theme';

export interface ThemeSettingsContextValue {
  primaryColor: string;
  updatePrimaryColor: (color: string) => Promise<void>;
  themeMode: ThemeMode;
  updateThemeMode: (mode: ThemeMode) => void;
  resolvedMode: 'dark' | 'light';
  theme: Theme;
  loading: boolean;
}

export const ThemeSettingsContext = createContext<ThemeSettingsContextValue | null>(null);

export function ThemeSettingsProvider({ children }: { children: ReactNode }) {
  const [primaryColor, setPrimaryColor] = useState<string>(DEFAULT_PRIMARY);
  const [themeMode, setThemeMode] = useState<ThemeMode>(getInitialMode);
  const [systemMode, setSystemMode] = useState(getSystemMode);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (event: MediaQueryListEvent) =>
      setSystemMode(event.matches ? 'dark' : 'light');
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    getThemePrimaryColor()
      .then((color) => {
        if (color) {
          setPrimaryColor(color);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const updatePrimaryColor = useCallback(async (color: string) => {
    setPrimaryColor(color);
    try {
      await updateThemePrimaryColor(color);
    } catch (error) {
      console.error('Error saving theme color:', error);
    }
  }, []);

  const updateThemeMode = useCallback((mode: ThemeMode) => {
    setThemeMode(mode);
    localStorage.setItem('themeMode', mode);
  }, []);

  const resolvedMode = themeMode === 'system' ? systemMode : themeMode;
  const theme = useMemo(() => buildTheme(primaryColor, resolvedMode), [primaryColor, resolvedMode]);

  const value = useMemo<ThemeSettingsContextValue>(
    () => ({
      primaryColor,
      updatePrimaryColor,
      themeMode,
      updateThemeMode,
      resolvedMode,
      theme,
      loading,
    }),
    [primaryColor, updatePrimaryColor, themeMode, updateThemeMode, resolvedMode, theme, loading]
  );

  return <ThemeSettingsContext.Provider value={value}>{children}</ThemeSettingsContext.Provider>;
}